import axios from "axios";
import config from "../constants/config";

const apiClient = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
  },
});

// ─── Token ──────────────────────────────────────────────────────────────────

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token || null;
}

export function getAccessToken() {
  return accessToken;
}

// ─── Refresh callbacks (registered by auth-session.store) ───────────────────

let onRefreshSuccessCb = null;
let onRefreshFailedCb = null;

export function registerRefreshCallbacks({ onSuccess, onFailed }) {
  onRefreshSuccessCb = onSuccess;
  onRefreshFailedCb = onFailed;
}

// ─── Request interceptor: attach Bearer ─────────────────────────────────────

apiClient.interceptors.request.use((config) => {
  if (accessToken && config?.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ─── 401 response interceptor: refresh + queue + retry ──────────────────────

let isRefreshing = false;
let queuedRequests = [];

function enqueueRequest(callback) {
  return new Promise((resolve, reject) => {
    queuedRequests.push({ resolve, reject, callback });
  });
}

function flushQueue(error, newToken = null) {
  queuedRequests.forEach((p) => {
    if (error) return p.reject(error);
    p.resolve(p.callback(newToken));
  });
  queuedRequests = [];
}

function isAuthEndpoint(url) {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/register") ||
    url.includes("/auth/logout") ||
    url.includes("/auth/refresh")
  );
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const status = error?.response?.status;

    if (status === 401 && !original._retry && !isAuthEndpoint(url)) {
      original._retry = true;

      if (isRefreshing) {
        return enqueueRequest((newToken) => {
          original.headers = {
            ...(original.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };
          return apiClient(original);
        });
      }

      isRefreshing = true;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/auth/refresh`,
          {},
          { withCredentials: true, timeout: 10000 },
        );

        const newToken = data?.data?.accessToken || data?.accessToken;
        const refreshTokenExpiresAt =
          data?.data?.refreshTokenExpiresAt || data?.refreshTokenExpiresAt;

        if (!newToken) throw new Error("No access token in refresh response");

        accessToken = newToken;
        onRefreshSuccessCb?.({ accessToken: newToken, refreshTokenExpiresAt });

        flushQueue(null, newToken);

        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(original);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        onRefreshFailedCb?.();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ─── Cross-tab logout signal ────────────────────────────────────────────────

export function signalLogout() {
  if (typeof window !== "undefined") {
    localStorage.setItem("app_logout_signal", Date.now().toString());
    setTimeout(() => {
      localStorage.removeItem("app_logout_signal");
    }, 100);
  }
}

export default apiClient;
